import { Button } from 'flowbite-react';
import { useEffect, useState } from 'react';
import {
	DayTimeSlot,
	DriveInvitee,
	DriveInviteeApplicationStatus,
	DriveInviteeDocument,
	SlotTime,
	WeekDay,
} from 'types';
import { EligibilityTimeSelection } from '../EligibilityTestComponent/EligibilityTimeSelection';
import CounsellingConfirmationPage from './CounsellingConfirmationPage';
import { useUserContext } from 'src/context/UserProvider';
import { useMutation, useQueryClient } from 'react-query';
import { driveRegistrationScheduleCounselling } from 'src/apis/drive';
import { useToast } from 'src/context/ToastProvider';
import { get } from 'lodash';
import dayjs from 'dayjs';
import ResultsPendingScreen from '../ResultsPendingScreen';

const CounsellingBooking = ({
	slots,
	driveRegistrationApplication,
}: {
	slots: DayTimeSlot[];
	driveRegistrationApplication: DriveInviteeDocument;
}) => {
	const toast = useToast();
	const { user } = useUserContext();
	const queryClient = useQueryClient();
	const [selectedDateAnyDay, setSelectedDateAndDay] = useState<DayTimeSlot | undefined>(undefined);
	const [cousellingSelectedTime, setCousellingSelectedTime] = useState<SlotTime | undefined>(undefined);

	useEffect(() => {
		if (slots.length > 0) {
			const nearestSlot = slots.reduce((prev, curr) => {
				return dayjs(curr.date).isBefore(dayjs(prev.date)) ? curr : prev;
			});
			setSelectedDateAndDay(nearestSlot);
		}
	}, [slots]);

	const scheduleConsellingForDrive = useMutation(['scheduleConsellingForDrive'], driveRegistrationScheduleCounselling, {
		onSuccess() {
			queryClient.invalidateQueries(['getDriveRegistrationApplication']);
			toast({
				title: 'Counselling Scheduled Successfully.',
				type: 'success',
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	return (
		<>
			{driveRegistrationApplication?.userApplicationDetails?.counsellingSlot ? (
				driveRegistrationApplication?.userApplicationDetails?.status ===
				DriveInviteeApplicationStatus.COUNSELLING_PENDING_RESULTS ? (
					<ResultsPendingScreen />
				) : (
					<>
						{/* Don't remove conditionality managing */}
						<CounsellingConfirmationPage
							scheduledTimeSlot={driveRegistrationApplication?.userApplicationDetails?.counsellingSlot}
							counsellingLink={driveRegistrationApplication?.userApplicationDetails?.counsellingLink || ''}
						/>
					</>
				)
			) : (
				<div className="flex justify-start">
					<div className="w-full">
						<div className="text-left mb-[10px]">
							<img
								src="https://masai-website-images.s3.ap-south-1.amazonaws.com/noto_confetti_ball_ezgif_com_png_to_webp_converter_b425c22fb7.webp"
								alt="Celebration"
								className="w-[64px] h-[64px]"
							/>
						</div>
						<h1 className="text-[32px] font-[500] text-[#3BAA73] text-left mb-[12px] leading-[40px]">
							Congratulations {user?.name}!
						</h1>
						<div className="text-left mb-[19px] text-[#0A0103] text-[16px] font-[500] leading-[24px]">
							You've successfully cleared the eligibility test for the{' '}
							<span className="font-[700]">{driveRegistrationApplication?.drive?.location} Placement Drive!</span>
							<br />
							We're thrilled to welcome you on board! 🚀
							<br />
							<br />
							You have been invited to a one-on-one counselling session with the Placement team. If you are a student,
							please attend this session with your parents.
						</div>
						<div className="mb-[18px]">
							<div className="mb-4">
								<span className="text-[#3B3435] text-[14px] font-[500] leading-[20px]">Counselling Date: </span>
								<span className="text-[#3470E4] text-[16px] font-[600] leading-[24px]">
									{selectedDateAnyDay?.day} {dayjs(selectedDateAnyDay?.date).format('Do MMM')}
								</span>
							</div>

							<div className="flex flex-col gap-4">
								<EligibilityTimeSelection
									selectedDaySlot={selectedDateAnyDay}
									selectedTimeSlot={cousellingSelectedTime}
									setSelectedTimeSlot={setCousellingSelectedTime}
									heading={'Available Slots'}
								/>
							</div>
						</div>

						{/* Footer Section */}
						<div className="mt-auto pt-2 fixed px-2 pb-1 lg:px-0 lg:sticky bottom-0 w-full inset-x-0 bg-[#FBFBFB] border-t tlg:border-t-0">
							<Button
								size="lg"
								className="w-full uppercase font-[600]"
								isProcessing={scheduleConsellingForDrive?.isLoading}
								onClick={() => {
									if (
										!driveRegistrationApplication?.userApplicationDetails?.counsellingLink &&
										cousellingSelectedTime &&
										selectedDateAnyDay
									) {
										scheduleConsellingForDrive.mutate({
											driveApplicationId: driveRegistrationApplication?._id,
											selectedCounsellingSlot: {
												startTime: dayjs(cousellingSelectedTime?.time || '').toDate(),
												endTime: dayjs(cousellingSelectedTime?.time || '')
													.add(Number(selectedDateAnyDay?.duration || 0), 'minutes')
													.toDate(),
												duration: Number(selectedDateAnyDay?.duration || 0),
												day: selectedDateAnyDay?.day || WeekDay.Monday,
											},
										});
									} else {
										toast({
											title: 'Counselling already scheduled',
											type: 'error',
										});
									}
								}}
								color="purplePlaced"
								disabled={!cousellingSelectedTime || scheduleConsellingForDrive?.isLoading}
							>
								BOOK COUNSELLING
							</Button>

							{/* Don't remove conditionality managing */}
							{/* <Button color="purplePlaced" className="w-full text-center text-[20px] font-[600] leading-[26px] py-[10px]" disabled={!cousellingSelectedTime}>
                            START COUNSELLING NOW
                        </Button> */}

							<p className="text-[#5D8DE9] text-[14px] leading-[24px] font-[600] mt-[8px]">
								Please note: This is a mandatory next step
							</p>

							<div className="text-left">
								{/* <span className="text-[#0A0103] font-inter text-[16px] leading-[24px] font-[600]">or </span>
                            <a href="#" className="text-[#3470E4] text-[14px] font-inter leading-[24px] font-[600] underline">
                                Start counselling now
                            </a> */}
								{/* Don't remove conditionality managing */}
								{/* <a href="#" className="text-[#3470E4] text-[14px] font-inter leading-[24px] font-[600] underline">
                            Schedule counselling for later
                        </a> */}
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default CounsellingBooking;
